/**
 * Pain Location Checklist — Accessible Alternative to Canvas Body Map
 *
 * WCAG 1.1.1 (A) compliance: text alternative for non-text content.
 * Uses <fieldset>/<legend> grouping with proper ARIA attributes.
 *
 * Both the canvas drawing AND this checklist write to the same
 * `selectedAreas` state via the `onChange` prop, so users can
 * freely switch between visual and text input modes.
 */

import React from 'react';

const PAIN_REGIONS = [
  {
    group: 'Head & Neck',
    areas: [
      'Head (Front)', 'Head (Back)',
      'Neck (Front)', 'Neck (Back)',
      'Jaw / TMJ',
    ],
  },
  {
    group: 'Upper Body',
    areas: [
      'Left Shoulder', 'Right Shoulder',
      'Upper Back', 'Chest',
      'Left Arm', 'Right Arm',
      'Left Elbow', 'Right Elbow',
      'Left Wrist / Hand', 'Right Wrist / Hand',
    ],
  },
  {
    group: 'Mid Body',
    areas: [
      'Mid Back', 'Lower Back',
      'Abdomen',
      'Left Hip', 'Right Hip',
      'Pelvis / Groin',
    ],
  },
  {
    group: 'Lower Body',
    areas: [
      'Left Upper Leg (Thigh)', 'Right Upper Leg (Thigh)',
      'Left Knee', 'Right Knee',
      'Left Lower Leg (Shin/Calf)', 'Right Lower Leg (Shin/Calf)',
      'Left Ankle / Foot', 'Right Ankle / Foot',
    ],
  },
];

const PainLocationChecklist = ({ selectedAreas = [], painNotes = '', painBySite = {}, onChange }) => {
  const handleToggle = (area) => {
    const updated = selectedAreas.includes(area)
      ? selectedAreas.filter((a) => a !== area)
      : [...selectedAreas, area];
    // Clean up painBySite when unchecking
    const updatedPainBySite = { ...painBySite };
    if (!updated.includes(area)) delete updatedPainBySite[area];
    onChange?.({ selectedAreas: updated, painNotes, painBySite: updatedPainBySite });
  };

  const handlePainLevel = (area, level) => {
    const updatedPainBySite = { ...painBySite, [area]: level };
    onChange?.({ selectedAreas, painNotes, painBySite: updatedPainBySite });
  };

  const handleNotesChange = (e) => {
    onChange?.({ selectedAreas, painNotes: e.target.value, painBySite });
  };

  return (
    <div
      role="region"
      aria-label="Pain location text checklist"
      className="pain-checklist-container"
    >
      <div className="pain-checklist-grid">
        {PAIN_REGIONS.map((region) => (
          <fieldset key={region.group} className="pain-checklist-group">
            <legend className="pain-checklist-legend">
              {region.group}
              <span className="pain-checklist-count">
                {region.areas.filter((a) => selectedAreas.includes(a)).length > 0
                  ? ` (${region.areas.filter((a) => selectedAreas.includes(a)).length} selected)`
                  : ''}
              </span>
            </legend>
            {region.areas.map((area) => {
              const isSelected = selectedAreas.includes(area);
              return (
                <div key={area}>
                  <label className={`pain-checklist-item ${isSelected ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(area)}
                      aria-label={`Pain in ${area}`}
                      className="pain-checklist-checkbox"
                    />
                    <span className="pain-checklist-label">
                      {area}
                      {isSelected && painBySite[area] != null && (
                        <span style={{ marginLeft: '6px', fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>
                          ({painBySite[area]}/10)
                        </span>
                      )}
                    </span>
                  </label>
                  {/* M-07: Per-site pain level when checked */}
                  {isSelected && (
                    <div style={{ marginLeft: '26px', marginBottom: '6px', display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                      {Array.from({ length: 11 }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handlePainLevel(area, i)}
                          aria-pressed={painBySite[area] === i}
                          aria-label={`${area} pain level ${i} out of 10`}
                          style={{
                            width: '28px', height: '28px', fontSize: '11px', fontWeight: 600,
                            border: `1px solid ${painBySite[area] === i ? 'var(--color-primary, #088395)' : '#d1d5db'}`,
                            borderRadius: '4px', cursor: 'pointer', fontFamily: 'inherit',
                            background: painBySite[area] === i ? 'var(--color-primary, #088395)' : '#fff',
                            color: painBySite[area] === i ? '#fff' : '#374151',
                          }}
                        >
                          {i}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </fieldset>
        ))}
      </div>

      {selectedAreas.length > 0 && (
        <div className="pain-checklist-summary" aria-live="polite">
          <strong>Selected ({selectedAreas.length}):</strong>{' '}
          {selectedAreas.join(', ')}
        </div>
      )}

      <div className="pain-checklist-notes">
        <label htmlFor="pain-notes-text" className="pain-checklist-notes-label">
          Describe your pain (optional):
        </label>
        <textarea
          id="pain-notes-text"
          value={painNotes}
          onChange={handleNotesChange}
          placeholder="e.g., Sharp pain in lower back radiating down left leg, dull ache in both knees..."
          aria-describedby="pain-notes-help"
          dir="auto"
          className="pain-checklist-textarea"
        />
        <p id="pain-notes-help" className="pain-checklist-help">
          Include details like: type of pain (sharp, dull, burning), when it occurs, and severity (1-10).
        </p>
      </div>
    </div>
  );
};

// Export regions for testing
PainLocationChecklist.PAIN_REGIONS = PAIN_REGIONS;

export default PainLocationChecklist;
